import React, { useState } from "react";
import { Form, Card, Button, Row, Col, Badge } from "react-bootstrap";
import {
  CheckCircle,
  CreditCard,
  Smartphone,
  Banknote,
  Shield,
  Clock,
  Star,
} from "lucide-react";
import "./CheckoutOptions.css";

interface CheckoutOption {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  features: string[];
  processingTime: string;
  fees: string;
  recommended?: boolean;
  popular?: boolean;
}

interface CheckoutOptionsProps {
  onOptionSelect: (optionId: string) => void;
  selectedOption?: string;
}

const CheckoutOptions: React.FC<CheckoutOptionsProps> = ({
  onOptionSelect,
  selectedOption,
}) => {
  const [selected, setSelected] = useState(selectedOption || "");

  const checkoutOptions: CheckoutOption[] = [
    {
      id: "credit-card",
      name: "Credit/Debit Card",
      description:
        "Pay securely with your Visa, Mastercard, or American Express",
      icon: <CreditCard size={24} />,
      features: [
        "Instant processing",
        "3D Secure protection",
        "All major cards accepted",
        "Mobile wallet support",
      ],
      processingTime: "Instant",
      fees: "No additional fees",
      popular: true,
    },
    {
      id: "bank-transfer",
      name: "Bank Transfer",
      description: "Direct transfer from your bank account for larger amounts",
      icon: <Banknote size={24} />,
      features: [
        "Lower fees for large amounts",
        "Direct from your bank",
        "ACH processing",
        "Up to $50,000 limit",
      ],
      processingTime: "1-3 business days",
      fees: "0.5% fee (min $2)",
      recommended: true,
    },
    {
      id: "digital-wallet",
      name: "Digital Wallet",
      description: "Pay with Apple Pay, Google Pay, or PayPal",
      icon: <Smartphone size={24} />,
      features: [
        "One-tap payment",
        "Biometric authentication",
        "Purchase protection",
        "Rewards integration",
      ],
      processingTime: "Instant",
      fees: "No additional fees",
    },
    {
      id: "crypto",
      name: "Cryptocurrency",
      description:
        "Pay with Bitcoin, Ethereum, or other supported cryptocurrencies",
      icon: <Shield size={24} />,
      features: [
        "Decentralized payment",
        "Lower transaction fees",
        "Privacy focused",
        "Global accessibility",
      ],
      processingTime: "10-30 minutes",
      fees: "Network fees only",
    },
  ];

  const handleOptionChange = (optionId: string) => {
    setSelected(optionId);
    onOptionSelect(optionId);
  };

  return (
    <div className="checkout-options">
      <div className="checkout-header mb-4">
        <h3 className="mb-2">Choose Payment Method</h3>
        <p className="text-muted">Select your preferred payment option below</p>
      </div>

      <Form>
        <Row className="g-3">
          {checkoutOptions.map((option) => (
            <Col key={option.id} md={6} lg={6}>
              <Card
                className={`checkout-option-card h-100 ${
                  selected === option.id ? "selected" : ""
                } ${option.recommended ? "recommended" : ""} ${
                  option.popular ? "popular" : ""
                }`}
                onClick={() => handleOptionChange(option.id)}
                style={{ cursor: "pointer" }}
              >
                <Card.Body className="p-4">
                  <div className="d-flex align-items-start mb-3">
                    <div className="option-icon me-3">{option.icon}</div>
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center mb-1">
                        <h5 className="mb-0 me-2">{option.name}</h5>
                        {option.recommended && (
                          <Badge bg="success" className="me-2">
                            <Star size={12} className="me-1" />
                            Recommended
                          </Badge>
                        )}
                        {option.popular && <Badge bg="primary">Popular</Badge>}
                      </div>
                      <p className="text-muted mb-3">{option.description}</p>
                    </div>
                    <Form.Check
                      type="radio"
                      name="checkoutOption"
                      id={option.id}
                      checked={selected === option.id}
                      onChange={() => handleOptionChange(option.id)}
                      className="ms-2"
                    />
                  </div>

                  <div className="option-features mb-3">
                    <h6 className="mb-2">Features:</h6>
                    <ul className="feature-list">
                      {option.features.map((feature, index) => (
                        <li key={index} className="feature-item">
                          <CheckCircle
                            size={14}
                            className="me-2 text-success"
                          />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="option-details">
                    <Row>
                      <Col xs={6}>
                        <div className="detail-item">
                          <Clock size={16} className="me-2 text-muted" />
                          <span className="detail-label">Processing:</span>
                          <span className="detail-value">
                            {option.processingTime}
                          </span>
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="detail-item">
                          <Shield size={16} className="me-2 text-muted" />
                          <span className="detail-label">Fees:</span>
                          <span className="detail-value">{option.fees}</span>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Form>

      {selected && (
        <div className="selected-option-summary mt-4">
          <Card className="border-success">
            <Card.Body className="p-3">
              <div className="d-flex align-items-center">
                <CheckCircle size={20} className="me-2 text-success" />
                <span>
                  <strong>Selected:</strong>{" "}
                  {checkoutOptions.find((opt) => opt.id === selected)?.name}
                </span>
              </div>
            </Card.Body>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CheckoutOptions;
